import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import Entity from '../hemFields/entity';
import Field from '../hemFields/field';
import EntityCollection from '../hemFields/entity-collection';
import CmsToCollegeCoreMapper from './mapper';
import fieldLinkagesJson from '../hemFields/field-links';
import 'isomorphic-fetch';
import CMSDataMapping from '../cmsDataMapping';

const documents = {
  college: {},
  admissions: {},
  offices: {},
  profile: {},
  tuition: {},
  deadlines: {},
  programs: {},
  statistics: {},
};

const language = 'und';

const CMSOverrides = {
  field_he_testtype_id: {
    displayRename: {
      'SAT Critical Reading': 'SAT Reading Writing',
    },
    displayNotAllowed: {
      'SAT Writing': true,
    },
  },
};

const contentTypePatchers = {
  he_transfer_admissions: (contentType) => {
    const countryRefField = contentType.metaData.find((item) => item.field_name === 'field_he_xfer_c_country_ref');
    /* istanbul ignore else */
    if (countryRefField) {
      Object.assign(countryRefField, {
        submit_value_vocabulary: 'country',
      });
    }
    const stateRefField = contentType.metaData.find((item) => item.field_name === 'field_he_xfer_c_state_ref');
    /* istanbul ignore else */
    if (stateRefField) {
      Object.assign(stateRefField, {
        submit_value_vocabulary: 'state',
      });
    }
    return contentType;
  },
};

class CollegeEditorService {
  constructor(
    college,
    jwtToken,
    cmsData,
    initializeBasicInfo,
    setBasicInfoIsLoaded,
    updateCMSRawData,
    setCMSDataIsInvalid,
    baseHemURL,
  ) {
    const isEditor = true;
    this.scid = college.codes.SCID;
    this.jwtToken = jwtToken;
    this.types = {};
    this.entities = {};
    this.removedEntities = [];
    this.changedValues = {};
    this.changedValuesToKeep = {};
    this.fieldObjects = {};
    this.entityObjects = {};
    this.entityCollections = [];
    this.tempEntityId = 0;
    this.linkedFields = [];
    this.esData = {};
    this.cmsData = cmsData;
    this.cmsDataState = 'CHECKING';
    this.esDataState = 'CHECKING';
    this.linkedFields = [];
    this.canEdit = isEditor;
    this.setBasicInfoIsLoaded = setBasicInfoIsLoaded;
    this.initializeBasicInfo = initializeBasicInfo;
    this.updateCMSRawData = updateCMSRawData;
    this.setCMSDataIsInvalid = setCMSDataIsInvalid;
    this.linkedFields = this.getAllFieldLinkages();
    this.mapper = new CmsToCollegeCoreMapper(this, documents);
    this.setBasicInfoIsLoaded(false);
    this.baseHemURL = baseHemURL;
    this.mapper.on('refresh', () => {
      noop(); // RLK
    });
  }

  load = async () => {
    await this.loadEsData();
    await this.loadCmsAndReset();
  };

  loadEsData = async () => {
    const fetchConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.jwtToken}`,
      },
    };
    const response = await fetch(`${process.env.COLLEGEPROFILE_API_URL}/${this.scid}`, fetchConfig);
    this.esData = await response.json();
    this.esDataState = 'LOADED';
    this.setBasicInfoIsLoaded(false);
  };

  loadCmsAndReset = async () => {
    const data = await this.loadCmsData();
    if (this.cmsDataState === 'LOADED') {
      this.initializeBasicInfo({ isLoaded: true, data: data.collegeCoreState });
      this.reset();
    }
  };

  searchByName = async (searchName) => {
    const fetchConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.jwtToken}`,
      },
    };
    const url = `${process.env.COLLEGEPROFILE_API_URL}/search-colleges-by-name?name=${searchName}&first=10`;
    const response = await fetch(url, fetchConfig);
    const body = await response.json();
    return get(body, 'edges', []).map(({ node }) => {
      const { name, scid, hiid, stateAbbr, country } = node;
      return {
        name,
        scid,
        hiid,
        country,
        state: stateAbbr,
      };
    });
  };

  getAllFieldLinkages = () => {
    const fields = {};
    Object.keys(fieldLinkagesJson).forEach((linkageField) => {
      const linkage = fieldLinkagesJson[linkageField];
      linkage.fields.forEach((field) => {
        fields[field] = linkage;
        fields[field].targetField = linkageField;
      });
    });
    return fields;
  }

  applyFieldLinkage = (field, eid) => {
    const linkage = this.linkedFields[field];
    /* istanbul ignore else */
    if (linkage.constraintType === 'PERCENT') {
      let total = 0;
      for (let i = 0; i < linkage.fields.length; i += 1) {
        const val = this.getValue(eid, linkage.fields[i]);
        /* istanbul ignore else */
        if (val && val.length && val[0]) {
          total += parseInt(val[0].value, 10);
        }
      }
      const newVal = 100 - total;
      this.setValue(eid, linkage.targetField, [{ value: newVal.toString() }]);
    }
  }

  getEntityRepresentationByEid = (eid) => this.entities[eid]

  getEntityRepresentationsByType = (type) => {
    const reps = Object.keys(this.entities)
      .map((eid) => this.entities[eid])
      .filter((entity) => entity._type === type);
    return reps;
  }

  getField = (eid, field) => {
    const key = eid + field;
    if (!(key in this.fieldObjects)) {
      this.fieldObjects[key] = new Field(this, eid, field);
    }
    return this.fieldObjects[key];
  }

  getFieldByTypeAndField = (type, field) => {
    const entities = this.getEntityRepresentationsByType(type);
    return entities.length === 1 ? this.getField(entities[0]._eid, `field_${field}`) : null;
  }

  getEntity = (eid) => {
    if (!(eid in this.entityObjects)) {
      this.entityObjects[eid] = new Entity(this, eid);
    }
    return this.entityObjects[eid];
  }

  getAllNodesByType = (type, parentType) => {
    let entities = this.getEntityRepresentationsByType(type);
    if (parentType) {
      const parentNid = ((this.getEntityRepresentationsByType(parentType)[0] || {})).nid;
      entities = entities.filter((entity) => entity.host_id === parentNid);
    }
    return entities.map((node) => this.getEntity(node._eid || node.eid));
  }

  getEntitiesByTypeAndFields = (type, values, parentEid, parentField) => {
    let entities = this.getEntityRepresentationsByType(type);
    if (parentEid && this.types[type].cmsContentType === 'fc') {
      const parentNid = this.entities[parentEid].nid;
      entities = entities.filter((e) => e.host_id === parentNid);
    } else if (parentEid && parentField && this.types[type].cmsContentType === 'node') {
      const nids = (this.getValue(parentEid, parentField) || []).map((i) => i.target_id);
      entities = entities.filter((e) => nids.includes(e.nid));
    }
    return entities.map((e) => {
      const entity = this.getEntity(e._eid || e.eid);
      return entity;
    });
  }

  getEntityCollectionByReference = (type, field, fields = {}) => {
    const entities = this.getEntityRepresentationsByType(type);
    if (entities.length === 0) {
      return null;
    }
    const entity = entities[0];
    const fieldMetadata = this.types[entity._type].fields[field];
    if (fieldMetadata.field_type === 'field_collection') {
      return this.getCachedEntityCollection(field, fields, entity._eid);
    } else if (fieldMetadata.field_type === 'entityreference') {
      const targetType = fieldMetadata.target_bundle ? fieldMetadata.target_bundle : fieldMetadata.target_bundles[0];
      return this.getCachedEntityCollection(targetType, fields, entity._eid, field);
    }
    return null;
  }

  getEntityCollectionByType = (type, fields= {}) => {
    if ('field_he_institution_ref' in this.types[type].fields) {
      fields.field_he_institution_ref = this.getEntityRepresentationsByType(
        this.types[type].fields.field_he_institution_ref.target_bundles[0],
      )
        .map((n) => ({ target_id: (n).nid }));
    }
    return this.getCachedEntityCollection(type, fields);
  }

  getCachedEntityCollection = (type, fields, parentEid, parentField) => {
    const key = { type, fields, parentEid, parentField };
    const entries = this.entityCollections.filter((ec) => isEqual(ec.key, key));
    if (entries.length > 0) {
      return entries[0].value;
    } else {
      const entityCollection = new EntityCollection(this, type, fields, parentEid, parentField);
      this.entityCollections.push({ key, value: entityCollection });
      return entityCollection;
    }
  }

  getFieldMetadata = (type, field) => this.types[type].fields[field];

  getMetadata = (eid, field) =>
    this.types[this.entities[eid]._type].fields[field] || this.types[this.entities[eid]._type].fields[`field_${field}`];

  getValue = (eid, field) => {
    if (eid in this.changedValues) {
      const changedNodeValues = this.changedValues[eid];

      if (field in changedNodeValues) {
        return changedNodeValues[field];
      }

      /* istanbul ignore if */
      if (field.startsWith('field_') && field.substring(6) in changedNodeValues) {
        return changedNodeValues[field.substring(6)];
      }
    }

    return this.getOriginalValue(eid, field);
  }

  getOriginalValue = (eid, field) => {
    const node = this.entities[eid];
    const values = node[field] ? node[field][language] : get(node, `field_${field}.${language}`);
    return values;
  }

  setValue = (eid, field, value, keepAfterReset = false) => {
    if (!(eid in this.changedValues)) {
      this.changedValues[eid] = {};
    }

    this.changedValues[eid][field] = value;
    if (keepAfterReset) {
      if (!this.changedValuesToKeep[eid]) {
        this.changedValuesToKeep[eid] = {};
      }
      this.changedValuesToKeep[eid][field] = value;
    }

    if (isEqual(value, this.getOriginalValue(eid, field))) {
      delete this.changedValues[eid][field];
      if (Object.keys(this.changedValues[eid]).length === 0) {
        delete this.changedValues[eid];
      }
    }

    /* istanbul ignore if */
    if (this.linkedFields[field]) {
      this.applyFieldLinkage(field, eid);
    }

    this.mapper.mergeField(this.entities[eid]._type, eid, field, value, this.entities[eid]);
  }

  addEntity = (type, values, parentEid, parentField) => {
    const metadata = this.types[type];
    const id = (this.tempEntityId -= 1).toString();
    const eid = metadata.cmsContentType + id;
    const object = {
      eid,
      _type: type,
    };
    if (parentEid && metadata.cmsContentType === 'fc') {
      object.item_id = id;
      object.host_id = this.entities[parentEid].nid;
      object.field_name = type;
    } else if (metadata.cmsContentType === 'node') {
      object.nid = id;
      object.type = type;
      object.title = '';
      if (parentEid && parentField) {
        const references = (this.getValue(parentEid, parentField) || []).slice();
        references.push({ target_id: id });
        this.setValue(parentEid, parentField, references);
      }
    }
    Object.keys(metadata.fields).forEach((fieldName) => {
      object[fieldName] = {};
    });
    Object.keys(values).forEach((fieldName) => {
      object[fieldName][language] = values[fieldName];
    });
    this.entities[eid] = object;
    Object.keys(metadata.fields).forEach((fieldName) => {
      this.mapper.mergeField(type, eid, fieldName, values[fieldName] || [], object);
    });
    return eid;
  }

  getReferencesToNode = (entity) => {
    const references = [];
    Object.keys(this.types).forEach((type) => {
      const { fields } = this.types[type];
      Object.keys(fields).forEach((field) => {
        if (fields[field].field_type === 'entityreference' && fields[field].target_type !== 'taxonomy_term') {
          if (fields[field].target_bundle === entity._type ||
            (fields[field].target_bundles && fields[field].target_bundles.includes(entity._type)) ||
            // field_he_xfer_ref does not currently have target_bundles property, adding an if condition for now
            (fields[field].field_name === 'field_he_xfer_ref' && entity._type === 'he_transfer_admissions')
          ) {
            this.getEntityRepresentationsByType(type).forEach((refEntity) => {
              const values = this.getValue(refEntity._eid, field) || [];
              values.forEach((v, i) => {
                if (v.target_id === this.getRealId(entity)) {
                  references.push({ eid: refEntity._eid, field });
                }
              });
            });
          }
        }
      });
    });
    return references;
  }

  removeEntity = (eid) => {
    const entity = this.entities[eid];
    this.removedEntities.push(entity);
    delete this.entities[eid];
    delete this.changedValues[eid];
    const metadata = this.types[entity._type];
    if (metadata.cmsContentType === 'node') {
      // Loop through everything that may reference it and remove the reference
      this.getReferencesToNode(entity).forEach((ref) => {
        const values = this.getValue(ref.eid, ref.field) || [];
        values.forEach((v, i) => {
          if (v.target_id === this.getRealId(entity)) {
            const newValue = values.slice();
            newValue.splice(i, 1);
            this.setValue(ref.eid, ref.field, newValue);
          }
        });
      });
    }
    Object.keys(metadata.fields).forEach((fieldName) => {
      this.mapper.mergeField(entity._type, eid, fieldName, [], entity);
    });
  }

  processCMSData = (response) => {
    this.types = {};
    this.entities = {};
    this.removedEntities = [];
    response.contentTypes.forEach((typeEntry) => {
      if (contentTypePatchers[typeEntry.typeName]) {
        contentTypePatchers[typeEntry.typeName](typeEntry);
      }
      this.types[typeEntry.typeName] = {
        cmsContentType: typeEntry.cmsType,
        coreType: typeEntry.coreType,
        fields: {},
      };
      typeEntry.metaData.forEach((field) => {
        if (field.field_type === 'list_integer' || field.field_type === 'list_boolean') {
          // Fix format of allowed_values (remove apostrophes from keys)
          field.allowed_values = Object.keys(field.allowed_values).reduce((newValues, id) => {
            const fieldOverride = CMSOverrides[field.field_name];
            if (fieldOverride) {
              if (fieldOverride.displayRename && fieldOverride.displayRename[field.allowed_values[id]]) {
                newValues[id.replace(/'/g, '')] = fieldOverride.displayRename[field.allowed_values[id]];
              } else if (!fieldOverride.displayNotAllowed || !fieldOverride.displayNotAllowed[field.allowed_values[id]]) {
                newValues[id.replace(/'/g, '')] = field.allowed_values[id];
              }
            } else {
              newValues[id.replace(/'/g, '')] = field.allowed_values[id];
            }
            return newValues;
          }, {});
        } else if (field.field_type === 'taxonomy_term_reference') {
          // Fill in allowed_values with real values for easier handling
          /* istanbul ignore else */
          if (response.vocabularies[field.allowed_values["'0'"].vocabulary] != null) {
            field.allowed_values = response.vocabularies[field.allowed_values["'0'"].vocabulary];
          }
        } else if (field.field_type === 'entityreference' && field.target_type === 'taxonomy_term') {
          // Fill in allowed_values with real values for easier handling
          field.allowed_values = field.target_bundles && field.target_bundles.length ? response.vocabularies[field.target_bundles[0]] : [];
        }
        this.types[typeEntry.typeName].fields[field.field_name] = field;
      });
      typeEntry.fieldValues.forEach((entity) => {
        const eid = typeEntry.cmsType + (entity.nid || entity.item_id);
        this.entities[eid] = {
          ...entity,
          _eid: eid,
          _type: typeEntry.typeName,
        };
      });
    });

    this.fixHostIds();
    this.mapper.init(this, this.types);

    this.changedValuesToKeep = {};
    this.cmsDataMapping = new CMSDataMapping(response, this);
    this.collegeCoreState = this.cmsDataMapping.getState();

    this.cmsDataState = 'LOADED';
    return { cmsDataState: this.cmsDataState, collegeCoreState: this.collegeCoreState };
  }

  loadCmsData = async () => {
    this.cmsDataState = 'LOADING';
    const isCMSInvalid = get(this.cmsData, 'isInvalid', true);
    const cmsRawData = get(this.cmsData, 'cmsRawData', null);
    const validTill = get(this.cmsData, 'validTill', null);
    if (!isCMSInvalid && cmsRawData && validTill && validTill >= Date.now()) {
      const cmsDataCopy = { ...cloneDeep(cmsRawData) };
      return this.processCMSData(cmsDataCopy);
    }
    try {
      const responseObj = await fetch(`${this.baseHemURL}/entity`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Jwt-Token': this.jwtToken,
        },
      });
      const response = await responseObj.json();
      this.updateCMSRawData({ ...cloneDeep(response) });
      this.setCMSDataIsInvalid(false);
      return this.processCMSData(response);
    } catch (err) {
      console.error(`Failed to load CMS data: ${err.message}`, err); // eslint-disable-line no-console
      this.cmsDataState = 'FAILED';
      return { cmsDataState: this.cmsDataState };
    }
  }

  fixHostIds = () => {
    Object.keys(this.types).forEach((typeName) => {
      const type = this.types[typeName];
      Object.keys(type.fields).forEach((fieldName) => {
        const field = type.fields[fieldName];
        if (field.field_type === 'field_collection') {
          this.getEntityRepresentationsByType(typeName).forEach((entity) => {
            /* istanbul ignore else */
            if (entity[fieldName] && entity[fieldName][language] && entity[fieldName][language].length > 0) {
              entity[fieldName][language].forEach((item) => {
                const fc = this.entities[`fc${item.value}`];
                if (fc && !fc.host_id) {
                  fc.host_id = entity.nid;
                }
              });
            }
          });
        }
      });
    });
  }

  makeOrGetCachedQuery = (key, url, transformer) => {
    const promise = this.mapper.getPromise(key);
    if (promise) {
      return promise.then(transformer);
    } else {
      return super.makeOrGetCachedQuery(key, url, transformer);
    }
  }

  // Resets all fields to the values received from CMS.
  // Doesn't reset added or deleted entities.
  reset = () => {
    // Discard all changes except newly added references if any
    this.changedValues = {};
    if (Object.keys(this.changedValuesToKeep).length > 0) {
      this.changedValues = cloneDeep(this.changedValuesToKeep);
    }
    if (this.cmsDataState === 'LOADED') {
      this.mapper.mergeAll(this.entities);
      // this.setBasicInfoIsLoaded(true);
    }
  }

  getRealId = (entity) => {
    if (entity.nid) {
      return entity.nid;
    } else {
      return entity.item_id;
    }
  };

  getAddObject = (eid) => {
    const entity = this.entities[eid];
    const object = {};
    if (entity.nid) {
      object.nid = entity.nid;
      object.type = entity.type;
    } else {
      object.item_id = entity.item_id;
      object.host_id = entity.host_id;
      object.field_name = entity.field_name;
    }
    const metadata = this.types[entity._type];
    Object.keys(metadata.fields).forEach((field) => {
      if (entity[field] && language in entity[field]) {
        const data = this.getSubmitRepresentation(entity[field][language], entity, field, 'add');
        if (!isEmpty(data)) {
          object[field] = {};
          object[field][language] = data;
        }
      }
    });
    const changes = this.changedValues[eid];
    Object.keys(changes).forEach((f) => {
      const field = f in entity ? f : `field_${f}`;
      object[field] = object[field] || {};
      object[field][language] = this.getSubmitRepresentation(changes[f], entity, field, 'add');
    });
    return object;
  }

  getUpdateObject = (eid) => {
    const entity = this.entities[eid];
    const object = entity.nid ?
      { nid: entity.nid } :
      { item_id: entity.item_id };
    const changes = this.changedValues[eid];
    Object.keys(changes).forEach((f) => {
      const field = f in entity ? f : `field_${f}`;
      object[field] = {};
      object[field][language] = this.getSubmitRepresentation(changes[f], entity, field, 'update');
    }, object);
    return object;
  }

  getRemoveObject = (entity) => (entity.nid ?
    { nid: entity.nid } :
    { item_id: entity.item_id });

  getSubmitRepresentation = (values, entity, field) => { // mode: 'add' | 'update') {
    if (values.length > 0) {
      const typeFields = this.types[entity._type].fields;
      const fieldDef = typeFields[field] || typeFields[`field_${field}`];
      const fieldType = fieldDef.field_type;
      if (this.types[entity._type].cmsContentType === 'node') {
        if (fieldDef.field_name === 'field_he_englishcompetencytestid') {
          // list_integer fields that accept multiple values should use a different format,
          // but we have no way to make a distinction based on just the metadata.
          return values.reduce((obj, item) => {
            obj[item.value] = item.value;
            return obj;
          }, {});
        } else if (fieldType === 'text_long' && values.find((item) => Array.isArray(item.value))) {
          // serializes the value for fields that contain JSON blobs in Drupal
          return values.map((item) =>
            ({
              ...item,
              value: item.value ? JSON.stringify(item.value, null, 4) : '',
            }));
        } else if (fieldType === 'list_boolean' || fieldType === 'list_integer' || fieldType === 'taxonomy_term_reference') {
          return values[0];
        } else if (fieldType === 'entityreference' && fieldDef.target_type === 'taxonomy_term') {
          if (fieldDef.submit_value_vocabulary) {
            const value = values;
            const taxonomyTerm = this.cmsDataMapping.vocabularies[fieldDef.submit_value_vocabulary][value];
            if (!taxonomyTerm) {
              throw new Error(`Vocabulary "${fieldDef.submit_value_vocabulary}" does not define ${value} for field ${fieldDef.field_name}`);
            }
            return [{ target_id: `${taxonomyTerm} - Taxonomy term (${value})` }];
          }
          return values.reduce((obj, item) => {
            obj[item.target_id] = item.target_id;
            return obj;
          }, {});
        }
      }
      if (fieldType === 'entityreference' && fieldDef.target_type !== 'taxonomy_term') {
        return values.map((item) => {
          const refEntity = this.entities[`node${item.target_id}`];
          return { target_id: `${refEntity.title} (${item.target_id})` };
        });
      }
    }
    return values;
  }

  saveAndLinkNewlyAddedEntities = async (rationale, eidsHavingReferences) => {
    const entities = eidsHavingReferences.map((eid) => this.entities[eid]);
    const added = eidsHavingReferences.map((eid) => this.getAddObject(eid));
    return fetch(
      this.baseHemURL,
      {
        method: 'POST',
        body: JSON.stringify({ rationale, added }),
        headers: {
          'Content-Type': 'application/json',
          'Jwt-Token': this.jwtToken,
          'Accept': 'application/json',
        },
      },
    ).then((res) => res.json())
      .then((response) => {
        /* istanbul ignore if */
        if (!response || response.success === false || response.errorMessage) {
          return Promise.reject(response.errorMessage);
        }

        entities.forEach((entity) => {
          const tempNid = entity.nid;
          const newNid = get(response, `addedEntityIds.${tempNid}`);
          const references = this.getReferencesToNode(entity);
          // TODO: update FC host_ids if we need to be able to add nodes that have field collections
          references.forEach(({ eid: refEid, field: refField }) => {
            const values = this.getValue(refEid, refField).slice();
            values.forEach((v, i) => {
              /* istanbul ignore else */
              if (v.target_id === tempNid) {
                values.splice(i, 1, { target_id: newNid });
              }
            });
            this.setValue(refEid, refField, values);
          });
          // Add the node using the new nid, but keep the old one in place too, so that things don't break.
          // (It will be cleaned up when data is reloaded after submit.)
          // To be removed when target_id references in save are fixed.
          this.entities[`node${newNid}`] = entity;
        });
        return response;
      });
  }

  // Sends all pending modifications to the CMS for persistance.
  save = async (rationale) => {
    const addedEids = Object.keys(this.changedValues)
      .filter((key) => parseInt(this.getRealId(this.entities[key]), 10) < 0)
      .filter((key) => key in this.changedValues);

    // Add the nodes which are referenced by some other nodes one by one before adding all the other ones
    const addedEidsHavingReferences = addedEids
      .filter((eid) => (this.entities[eid]).nid)
      .filter((eid) => this.getReferencesToNode(this.entities[eid]).length > 0);

    /* istanbul ignore else */
    if (addedEidsHavingReferences && addedEidsHavingReferences.length > 0) {
      addedEidsHavingReferences.forEach((eid) => addedEids.splice(addedEids.indexOf(eid), 1));
      await this.saveAndLinkNewlyAddedEntities(rationale, addedEidsHavingReferences);
    }

    const added = addedEids
      .map((key) => this.getAddObject(key));
    const updated = Object.keys(this.changedValues)
      .filter((key) => (this.getRealId(this.entities[key])) >= 0)
      .map(/* istanbul ignore next */(key) => this.getUpdateObject(key));
    /* istanbul ignore next */
    const removed = this.removedEntities
      .filter((entity) => this.getRealId(entity) >= 0)
      .map((entity) => this.getRemoveObject(entity));
    const data = { rationale, updated, added, removed };
    await fetch(
      this.baseHemURL,
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          'Jwt-Token': this.jwtToken,
          'Accept': 'application/json',
        },
      },
    ).then((res) => res.json())
      .then((response) => {
        /* istanbul ignore if */
        if (!response || response.success === false || response.errorMessage) {
          return Promise.reject(response.errorMessage);
        }
        this.setBasicInfoIsLoaded(false);
        this.updateCMSRawData(null);
        this.setCMSDataIsInvalid(true);
        this.loadCmsAndReset();

        return response;
      });
  }

  hasChanges = () => Object.keys(this.changedValues)
    .filter((eid) => {
      const entity = this.entities[eid];
      const changes = this.changedValues[eid];
      return Object.keys(changes).some((fieldName) => {
        const field = this.types[entity._type].fields[fieldName];
        return !(field.field_type === 'entityreference' && field.target_type !== 'taxonomy_term');
      });
    })
    .concat(this.removedEntities.filter((e) => this.getRealId(e) >= 0).map((e) => e._eid))
    .length > 0

  removePendingEntities = () => {
    Object.keys(this.entities)
      .filter((eid) => parseInt(this.getRealId(this.entities[eid]), 10) < 0)
      .filter((eid) => !(eid in this.changedValues))
      .forEach((eid) => this.removeEntity(eid));
  }

  getChangedContentTypesRequiringApproval = () => Object.keys(this.changedValues)
    .map((eid) => get(this.entities, `${eid}._type`, null))
    .filter((item) => item)
    .concat(
      Object.keys(this.changedValues)
        .map((eid) => get(this.entities, `${eid}.host_id`, null))
        .filter((nid) => nid)
        .map((nid) => this.entities[`node${nid}`])
        .map((node) => node.type),
    )
    .concat(
      this.removedEntities
        .filter((e) => parseInt(this.getRealId(e), 10) >= 0)
        .map((e) => e._type),
    )
    .filter((type) => get(this.types, `${type}.coreType`, false) === true)
    .reduce((accumulator, type) => {
      if (!accumulator.includes(type)) {
        accumulator.push(type);
      }
      return accumulator;
    }, []);
}

export default CollegeEditorService;
